import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../ui/Button';
import { translations } from '../../translations';

const Navbar = () => {
  const { lang = 'nl' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const t = translations[lang] || translations.nl;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const changeLanguage = (newLang) => {
    if (newLang === lang) return;
    const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
    navigate(newPath);
  };

  const navLinks = [
    { name: t.nav.howItWorks, path: `/${lang}/${lang === 'nl' ? 'hoe-het-werkt' : 'how-it-works'}` },
    { name: t.nav.situations, path: `/${lang}/${lang === 'nl' ? 'situaties' : 'situations'}` },
    { name: t.nav.international, path: `/${lang}/${lang === 'nl' ? 'internationaal' : 'international'}` },
    { name: t.nav.knowledge, path: `/${lang}/${lang === 'nl' ? 'kennisbank' : 'insights'}` },
    { name: t.nav.faq, path: `/${lang}/faq` },
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-[9999] bg-white transition-all duration-300 ease-in-out ${scrolled ? 'py-3 border-b border-slate-100 shadow-sm' : 'py-5 border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to={`/${lang}`} className="flex items-center gap-2.5 group relative z-[10001]">
          <div className="w-9 h-9 bg-navy-900 rounded-xl flex items-center justify-center group-hover:scale-105 transition-all shadow-inner-light">
            <SafeIcon name="Shield" className="text-white text-lg" />
          </div>
          <span className="font-bold text-xl tracking-tight text-navy-900">Testamentair</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8 bg-slate-50/80 px-6 py-2 rounded-full border border-slate-200/40">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path} 
              className={`text-[13px] font-semibold transition-colors relative py-1 ${location.pathname === link.path ? 'text-navy-900' : 'text-slate-500 hover:text-navy-900'}`}
            >
              {link.name}
              {location.pathname === link.path && (
                <motion.div layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-navy-900 rounded-full" transition={{ type: "spring", bounce: 0.2, duration: 0.6 }} />
              )}
            </Link>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-6 relative z-[10001]">
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
            <button 
              onClick={() => changeLanguage('nl')} 
              className={`px-2 py-0.5 rounded transition-colors ${lang === 'nl' ? 'text-navy-900 bg-navy-50' : 'hover:text-navy-900'}`}
            >
              NL
            </button>
            <button 
              onClick={() => changeLanguage('en')} 
              className={`px-2 py-0.5 rounded transition-colors ${lang === 'en' ? 'text-navy-900 bg-navy-50' : 'hover:text-navy-900'}`}
            >
              EN
            </button>
          </div>
          <Link to={`/${lang}/scan`}>
            <Button variant="primary" className="py-2.5 px-6 text-xs uppercase tracking-widest">{t.nav.startScan}</Button>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button className="lg:hidden text-navy-900 p-2 relative z-[10001]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          <SafeIcon name={mobileMenuOpen ? "X" : "Menu"} className="text-2xl" />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-0 left-0 w-full bg-white border-b border-slate-200 shadow-2xl lg:hidden z-[9998] pt-24">
            <div className="flex flex-col px-6 pb-12 space-y-6">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className={`text-2xl font-bold transition-colors ${location.pathname === link.path ? 'text-navy-900' : 'text-slate-400'}`}>
                  {link.name}
                </Link>
              ))}
              <div className="flex gap-4 pt-4">
                <button onClick={() => changeLanguage('nl')} className={`text-sm font-bold ${lang === 'nl' ? 'text-navy-900' : 'text-slate-400'}`}>NEDERLANDS</button>
                <button onClick={() => changeLanguage('en')} className={`text-sm font-bold ${lang === 'en' ? 'text-navy-900' : 'text-slate-400'}`}>ENGLISH</button>
              </div>
              <div className="pt-6 border-t border-slate-100">
                <Link to={`/${lang}/scan`}>
                  <Button variant="primary" className="w-full py-4 text-lg">{t.nav.startScan}</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;